// How a reading's buyers, sellers and lender go onto a form that may already have some: what the agent
// typed is kept or asked about, and what an earlier reading filled is replaced or cleared.
import { LenderInfo, PartyFields } from '../../../types/orderRequest'

const filled = (v: unknown) => typeof v === 'string' ? v.trim() !== '' : Boolean(v)
const personHas = (x?: { FirstName?: string; MiddleName?: string; LastName?: string; Email?: string; Phone?: string }) =>
  Boolean(x && [x.FirstName, x.MiddleName, x.LastName, x.Email, x.Phone].some(filled))
export const addressHas = (a?: PartyFields['Address']) => Boolean(a && (a.SameAsProperty || [a.Address1, a.Address2, a.City, a.State, a.Zip].some(filled)))

/** True only when nothing at all has been entered for the party (names, contact details, organization, address). */
export function blankParty(p: PartyFields): boolean {
  return !filled(p.OrganizationName) && !filled(p.OrganizationType) && !personHas(p.Primary) && !personHas(p.Spouse)
    && !(p.OrgPeople ?? []).some(personHas) && !addressHas(p.Address)
}

/** Who a party is, for matching a contract's party to one already on the form (not their position): the
 *  organization's name, or the person's full name with any middle names, so John A. and John B. Smith differ. */
export function partyKey(p: PartyFields): string {
  const name = p.Type === 'organization' ? p.OrganizationName
    : [p.Primary?.FirstName, p.Primary?.MiddleName, p.Primary?.LastName].join(' ')
  return String(name ?? '').normalize('NFC').toLocaleLowerCase().replace(/\s+/g, ' ').trim()
}

const counts = (keys: string[]) => keys.reduce((m, k) => m.set(k, (m.get(k) ?? 0) + 1), new Map<string, number>())

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

export type PartyPlan =
  | { kind: 'none' }
  | { kind: 'clear' }
  | { kind: 'replace'; parties: PartyFields[]; ask: boolean; droppedAddresses: number }

/** What to do with one side's parties. `prev` is what the last reading put there, if anything. */
export function planParties(current: PartyFields[], incoming: PartyFields[], prev?: PartyFields[]): PartyPlan {
  const untouched = current.every(blankParty) || (prev !== undefined && same(current, prev))
  if (!incoming.length) {
    // This contract names none: undo the last reading's, unless the agent has changed them or added an address.
    return prev && same(current, prev) && !current.some((p) => addressHas(p.Address)) ? { kind: 'clear' } : { kind: 'none' }
  }
  // The reader doesn't read contact addresses, so a party already on the form keeps its address, matched
  // by full name. An address is carried over only when exactly one party on each side has that name;
  // otherwise it can't be told whose it is, so it isn't copied and counts as dropped.
  const have = counts(current.map(partyKey))
  const want = counts(incoming.map(partyKey))
  const unique = (k: string) => k !== '' && have.get(k) === 1 && want.get(k) === 1
  const byKey = new Map(current.filter((c) => unique(partyKey(c))).map((c) => [partyKey(c), c]))
  const parties = incoming.map((p) => ({ ...p, Address: byKey.get(partyKey(p))?.Address ?? p.Address }))
  const droppedAddresses = current.filter((c) => addressHas(c.Address) && !unique(partyKey(c))).length
  // Never drop an address without asking, even when the parties are otherwise what the last reading put there.
  return { kind: 'replace', parties, ask: !untouched || droppedAddresses > 0, droppedAddresses }
}

/** The form values a reading would overwrite that are the agent's, differing from the reading's value:
 *  - a value the last reading set that the agent has since changed, even to blank or unticked (that's a correction);
 *  - otherwise anything entered at all (an empty field or unticked box with no reading behind it is nothing entered). */
export function valueConflicts(incoming: Record<string, unknown>, current: (name: string) => unknown,
  lastApplied: Record<string, unknown>): string[] {
  return Object.entries(incoming).filter(([name, value]) => {
    const cur = current(name)
    if (same(cur, value)) return false
    return name in lastApplied ? !same(cur, lastApplied[name]) : filled(cur)
  }).map(([name]) => name)
}

export const LENDER_READ = ['LenderName', 'LoanAmount', 'FinancingType'] as const
export type LenderRead = typeof LENDER_READ[number]

/** The first lender after a reading. Each value the reader reads is handled on its own: set from this
 *  reading, or cleared if the last reading set it and the agent hasn't changed it. `applied` records only
 *  the values a reading actually set, so values the agent typed are never taken for the reader's. */
export function planLender(current: LenderInfo | undefined, incoming: LenderInfo | null, applied: Partial<Record<LenderRead, string>>) {
  const base: LenderInfo = { LenderName: '', LoanAmount: '', LoanNumber: '', FinancingType: '', LenderFirstName: '', LenderLastName: '',
    LenderEmail: '', LenderPhone: '' }
  const next: LenderInfo = { ...base, ...(current ?? {}) }
  const nowApplied: Partial<Record<LenderRead, string>> = {}
  const conflicts: LenderRead[] = []
  let changed = false
  for (const k of LENDER_READ) {
    const inc = incoming?.[k] ?? ''
    const cur = next[k] ?? ''
    const was = applied[k]
    if (inc) {
      if (cur && cur !== inc && cur !== was) conflicts.push(k)
      if (cur !== inc) changed = true
      next[k] = inc
      nowApplied[k] = inc
    } else if (was !== undefined && cur === was && cur) {
      next[k] = ''
      changed = true
    }
  }
  const empty = !Object.entries(next).some(([k, v]) => k !== 'SameAsRole' && filled(v))
  return { next, applied: nowApplied, conflicts, changed, empty }
}
